import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"

import type {
  Body_authentication_login_access_token,
  Message,
  NewPassword,
  Token,
  UserPrivate,
  CitiesPublic,
  FormsContactCreate,
  FormsContactOrderBy,
  FormsContactPrivate,
  FormsContactsPrivate,
  FormsNewsletterCreate,
  FormsNewsletterOrderBy,
  FormsNewsletterPrivate,
  FormsNewslettersPreview,
  FormsNewsletterUnsubscribe,
  FormsNewsletterVerify,
  FormsSubscriptionCreate,
  FormsSubscriptionOrderBy,
  FormsSubscriptionPrivate,
  FormsSubscriptionsPrivate,
  SubscriptionPublic,
  TagCreate,
  TagPrivate,
  TagsOrderBy,
  TagsPrivate,
  TagsPublic,
  TagUpdate,
  AuthorCreate,
  AuthorPrivate,
  AuthorPublic,
  AuthorsOrderBy,
  AuthorsPrivate,
  AuthorUpdate,
  PolicyPublic,
  ImprintPublic,
  AGBPublic,
  PublicationCreate,
  PublicationPrivate,
  PublicationPublic,
  PublicationsOrderBy,
  PublicationsPrivate,
  PublicationsPublic,
  PublicationUpdate,
  PublicPublicationsOrderBy,
  LandingpagePublic,
  RateChartsPublic,
  NavigationsPublic,
  SettingsMimeTypeCreate,
  SettingsMimeTypePrivate,
  SettingsMimeTypesPrivate,
  SettingsMimeTypeUpdate,
  NextPublishDTPrivate,
  SettingsCommonPrivate,
  SettingsCommonUpdate,
  SettingsPrioCategoriesPrivate,
  SettingsPrioCategoriesUpdate,
  SettingsLandingpageUpdateRequest,
  SettingsLandingpageUpdateResponse,
  SettingsSubscriptionPrivate,
  SettingsSubscriptionUpdate,
  SettingsRateChartCreate,
  SettingsRateChartPrivate,
  SettingsRateChartsPrivate,
  SettingsRateChartUpdate,
  SettingsNavigationsPrivate,
  SettingsNavigationsUpdate,
  UpdatePassword,
  UserCreate,
  UsersOrderBy,
  UsersPrivate,
  UserUpdate,
  UserUpdateMe,
  Body_media_create_media,
  MediaItem,
  MediaOrderBy,
  MediaPreview,
  AuthorGroup,
  AuthorGroupCreate,
  AuthorGroupPrivate,
  AuthorGroups,
  AuthorGroupsOrderBy,
  AuthorGroupUpdate,
  PermissionsPrivate,
  PermsOrderBy,
  RoleCreate,
  RolePrivate,
  RolesOrderBy,
  RolesPreview,
  RoleUpdate,
  CategoriesOrderBy,
  CategoriesPrivate,
  CategoryCreate,
  CategoryPrivate,
  CategoryUpdate,
} from "./models"

export type TDataLoginAccessToken = {
  formData: Body_authentication_login_access_token
}
export type TDataRecoverPassword = {
  email: string
}
export type TDataResetPassword = {
  requestBody: NewPassword
}

export class AuthenticationService {
  /**
   * Login Access Token
   * OAuth2 compatible token login, get an access token for future requests
   * @returns Token Successful Response
   * @throws ApiError
   */
  public static loginAccessToken(
    data: TDataLoginAccessToken,
  ): CancelablePromise<Token> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/login/access-token",
      formData: formData,
      mediaType: "application/x-www-form-urlencoded",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Test Token
   * Test access token
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static testToken(): CancelablePromise<UserPrivate> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/login/test-token",
    })
  }

  /**
   * Recover Password
   * Password Recovery
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static recoverPassword(
    data: TDataRecoverPassword,
  ): CancelablePromise<Message> {
    const { email } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/password-recovery/{email}",
      path: {
        email,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Reset Password
   * Reset password
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static resetPassword(
    data: TDataResetPassword,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/reset-password/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataTestEmail = {
  emailTo: string
}

export class UtilsService {
  /**
   * Test Email
   * Test emails.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static testEmail(data: TDataTestEmail): CancelablePromise<Message> {
    const { emailTo } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/utils/test-email/",
      query: {
        email_to: emailTo,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Health Check
   * @returns boolean Successful Response
   * @throws ApiError
   */
  public static healthCheck(): CancelablePromise<boolean> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/health-check/",
    })
  }

  /**
   * Module Versions
   * Get module versions.
   * @returns string Successful Response
   * @throws ApiError
   */
  public static moduleVersions(): CancelablePromise<Record<string, string>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/module-versions/",
    })
  }

  /**
   * Trigger Error
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static triggerError(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/sentry-debug",
    })
  }
}

export type TDataCeleryTaskStatus = {
  taskId: string
}

export class CeleryService {
  /**
   * Celery Task Status
   * Get status for given Celery task-id
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static celeryTaskStatus(
    data: TDataCeleryTaskStatus,
  ): CancelablePromise<Record<string, unknown>> {
    const { taskId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/celery/{task_id}",
      path: {
        task_id: taskId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadCities = {
  cityName?: string | null
  postcode?: string | null
}

export class CitiesService {
  /**
   * Read Cities
   * Retrieve cities.
   * @returns CitiesPublic Successful Response
   * @throws ApiError
   */
  public static readCities(
    data: TDataReadCities = {},
  ): CancelablePromise<CitiesPublic> {
    const { cityName, postcode } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/cities/",
      query: {
        city_name: cityName,
        postcode,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsContact = {
  recaptchaToken: string
  requestBody: FormsContactCreate
}
export type TDataReadFormsContact = {
  descending?: boolean
  limit?: number
  orderBy?: FormsContactOrderBy
  query?: string | null
  skip?: number
}
export type TDataReadFormsContactById = {
  contactId: string
}
export type TDataDeleteFormsContact = {
  contactId: string
}

export class FormsContactService {
  /**
   * Create Forms Contact
   * Create new contact form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsContact(
    data: TDataCreateFormsContact,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/contact/",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Forms Contact
   * Retrieve contact form submissions.
   * @returns FormsContactsPrivate Successful Response
   * @throws ApiError
   */
  public static readFormsContact(
    data: TDataReadFormsContact = {},
  ): CancelablePromise<FormsContactsPrivate> {
    const {
      descending = true,
      limit = 100,
      orderBy = "created_at",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/contact/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Forms Contact By Id
   * Get a specific contact form submission by ID.
   * @returns FormsContactPrivate Successful Response
   * @throws ApiError
   */
  public static readFormsContactById(
    data: TDataReadFormsContactById,
  ): CancelablePromise<FormsContactPrivate> {
    const { contactId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/contact/{contact_id}",
      path: {
        contact_id: contactId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Forms Contact
   * Delete a contact form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsContact(
    data: TDataDeleteFormsContact,
  ): CancelablePromise<Message> {
    const { contactId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/forms/contact/{contact_id}",
      path: {
        contact_id: contactId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsNewsletter = {
  recaptchaToken: string
  requestBody: FormsNewsletterCreate
}
export type TDataFormsNewsletterUnsubscribe = {
  recaptchaToken: string
  requestBody: FormsNewsletterUnsubscribe
}
export type TDataFormsNewsletterVerify = {
  requestBody: FormsNewsletterVerify
}
export type TDataReadFormsNewsletters = {
  descending?: boolean
  limit?: number
  orderBy?: FormsNewsletterOrderBy
  query?: string | null
  showUnconfirmed?: boolean
  skip?: number
}
export type TDataReadFormsNewsletterById = {
  newsletterId: string
}
export type TDataDeleteFormsNewsletter = {
  newsletterId: string
}

export class FormsNewsletterService {
  /**
   * Create Forms Newsletter
   * Create new newsletter form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsNewsletter(
    data: TDataCreateFormsNewsletter,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/subscribe",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Forms Newsletter Unsubscribe
   * Request token to unsubscribe from newsletter
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static formsNewsletterUnsubscribe(
    data: TDataFormsNewsletterUnsubscribe,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/unsubscribe",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Forms Newsletter Verify
   * Verify newsletter subscribe/unsubscribe tokens.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static formsNewsletterVerify(
    data: TDataFormsNewsletterVerify,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/verify",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Forms Newsletters
   * Retrieve newsletter form submissions.
   * @returns FormsNewslettersPreview Successful Response
   * @throws ApiError
   */
  public static readFormsNewsletters(
    data: TDataReadFormsNewsletters = {},
  ): CancelablePromise<FormsNewslettersPreview> {
    const {
      descending = true,
      limit = 100,
      orderBy = "created_at",
      query,
      showUnconfirmed = true,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/newsletter/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        show_unconfirmed: showUnconfirmed,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Export Newsletters
   * Retrieve newsletter subscribers as XLSX file.
   * @returns binary Exported XLSX file
   * @throws ApiError
   */
  public static exportNewsletters(): CancelablePromise<Blob | File> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/newsletter/export",
    })
  }

  /**
   * Read Forms Newsletter By Id
   * Get a specific newsletter form submission by ID.
   * @returns FormsNewsletterPrivate Successful Response
   * @throws ApiError
   */
  public static readFormsNewsletterById(
    data: TDataReadFormsNewsletterById,
  ): CancelablePromise<FormsNewsletterPrivate> {
    const { newsletterId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/newsletter/{newsletter_id}",
      path: {
        newsletter_id: newsletterId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Forms Newsletter
   * Delete a newsletter form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsNewsletter(
    data: TDataDeleteFormsNewsletter,
  ): CancelablePromise<Message> {
    const { newsletterId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/forms/newsletter/{newsletter_id}",
      path: {
        newsletter_id: newsletterId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsSubscription = {
  recaptchaToken: string
  requestBody: FormsSubscriptionCreate
}
export type TDataReadFormsSubscriptions = {
  descending?: boolean
  limit?: number
  orderBy?: FormsSubscriptionOrderBy
  query?: string | null
  skip?: number
}
export type TDataReadFormsSubscriptionById = {
  subscriptionId: string
}
export type TDataDeleteFormsSubscription = {
  subscriptionId: string
}

export class FormsSubscriptionService {
  /**
   * Create Forms Subscription
   * Create new subscription form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsSubscription(
    data: TDataCreateFormsSubscription,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/subscription/",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Forms Subscriptions
   * Retrieve subscription form submissions.
   * @returns FormsSubscriptionsPrivate Successful Response
   * @throws ApiError
   */
  public static readFormsSubscriptions(
    data: TDataReadFormsSubscriptions = {},
  ): CancelablePromise<FormsSubscriptionsPrivate> {
    const {
      descending = true,
      limit = 100,
      orderBy = "created_at",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/subscription/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Forms Subscription By Id
   * Get a specific subscription form submission by ID.
   * @returns FormsSubscriptionPrivate Successful Response
   * @throws ApiError
   */
  public static readFormsSubscriptionById(
    data: TDataReadFormsSubscriptionById,
  ): CancelablePromise<FormsSubscriptionPrivate> {
    const { subscriptionId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/forms/subscription/{subscription_id}",
      path: {
        subscription_id: subscriptionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Forms Subscription
   * Delete a subscription form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsSubscription(
    data: TDataDeleteFormsSubscription,
  ): CancelablePromise<Message> {
    const { subscriptionId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/forms/subscription/{subscription_id}",
      path: {
        subscription_id: subscriptionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class SubscriptionService {
  /**
   * Read Subscription
   * Retrieve public subscription data.
   * @returns SubscriptionPublic Successful Response
   * @throws ApiError
   */
  public static readSubscription(): CancelablePromise<SubscriptionPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/subscription/",
    })
  }
}

export type TDataReadTagsPub = {
  skip?: number
}
export type TDataReadTags = {
  descending?: boolean
  limit?: number
  orderBy?: TagsOrderBy
  query?: string | null
  skip?: number
}
export type TDataCreateTag = {
  requestBody: TagCreate
}
export type TDataReadTagById = {
  tagId: string
}
export type TDataUpdateTag = {
  requestBody: TagUpdate
  tagId: string
}
export type TDataDeleteTag = {
  tagId: string
}

export class TagsService {
  /**
   * Read Tags
   * Retrieve tags.
   * @returns TagsPublic Successful Response
   * @throws ApiError
   */
  public static readTagsPub(
    data: TDataReadTagsPub = {},
  ): CancelablePromise<TagsPublic> {
    const { skip = 0 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/tags/",
      query: {
        skip,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Tags
   * Retrieve tags.
   * @returns TagsPrivate Successful Response
   * @throws ApiError
   */
  public static readTags(
    data: TDataReadTags = {},
  ): CancelablePromise<TagsPrivate> {
    const {
      descending = false,
      limit = 100,
      orderBy = "name",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/tags/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Tag
   * Create new tag.
   * @returns TagPrivate Successful Response
   * @throws ApiError
   */
  public static createTag(data: TDataCreateTag): CancelablePromise<TagPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/tags/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Tag By Id
   * Get a specific tag by id.
   * @returns TagPrivate Successful Response
   * @throws ApiError
   */
  public static readTagById(
    data: TDataReadTagById,
  ): CancelablePromise<TagPrivate> {
    const { tagId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/tags/{tag_id}",
      path: {
        tag_id: tagId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Tag
   * Update a tag.
   * @returns TagPrivate Successful Response
   * @throws ApiError
   */
  public static updateTag(data: TDataUpdateTag): CancelablePromise<TagPrivate> {
    const { requestBody, tagId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/tags/{tag_id}",
      path: {
        tag_id: tagId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Tag
   * Delete a tag.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteTag(data: TDataDeleteTag): CancelablePromise<Message> {
    const { tagId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/tags/{tag_id}",
      path: {
        tag_id: tagId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadAuthorPublic = {
  authorId: string
}
export type TDataReadAuthors = {
  authorgroupName?: string | null
  descending?: boolean
  limit?: number
  orderBy?: AuthorsOrderBy
  query?: string | null
  skip?: number
}
export type TDataCreateAuthor = {
  requestBody: AuthorCreate
}
export type TDataExportAuthors = {
  authorgroupName?: string | null
  descending?: boolean
  orderBy?: AuthorsOrderBy
  query?: string | null
}
export type TDataReadAuthorById = {
  authorId: string
}
export type TDataUpdateAuthor = {
  authorId: string
  requestBody: AuthorUpdate
}
export type TDataDeleteAuthor = {
  authorId: string
}

export class AuthorsService {
  /**
   * Read Author
   * Retrieve public author.
   * @returns AuthorPublic Successful Response
   * @throws ApiError
   */
  public static readAuthorPublic(
    data: TDataReadAuthorPublic,
  ): CancelablePromise<AuthorPublic> {
    const { authorId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/author/{author_id}",
      path: {
        author_id: authorId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Authors
   * Retrieve authors.
   * @returns AuthorsPrivate Successful Response
   * @throws ApiError
   */
  public static readAuthors(
    data: TDataReadAuthors = {},
  ): CancelablePromise<AuthorsPrivate> {
    const {
      authorgroupName,
      descending = false,
      limit = 100,
      orderBy = "lastname",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/authors/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
        authorgroup_name: authorgroupName,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Author
   * Create new author.
   * @returns AuthorPrivate Successful Response
   * @throws ApiError
   */
  public static createAuthor(
    data: TDataCreateAuthor,
  ): CancelablePromise<AuthorPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/authors/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Export Authors
   * Retrieve authors as XLSX file.
   * @returns binary Exported XLSX file
   * @throws ApiError
   */
  public static exportAuthors(
    data: TDataExportAuthors = {},
  ): CancelablePromise<Blob | File> {
    const {
      authorgroupName,
      descending = false,
      orderBy = "lastname",
      query,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/authors/export",
      query: {
        order_by: orderBy,
        descending,
        query,
        authorgroup_name: authorgroupName,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Author By Id
   * Get a specific author by ID.
   * @returns AuthorPrivate Successful Response
   * @throws ApiError
   */
  public static readAuthorById(
    data: TDataReadAuthorById,
  ): CancelablePromise<AuthorPrivate> {
    const { authorId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/authors/{author_id}",
      path: {
        author_id: authorId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Author
   * Update an existing author.
   * @returns AuthorPrivate Successful Response
   * @throws ApiError
   */
  public static updateAuthor(
    data: TDataUpdateAuthor,
  ): CancelablePromise<AuthorPrivate> {
    const { authorId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/authors/{author_id}",
      path: {
        author_id: authorId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Author
   * Delete an author.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAuthor(
    data: TDataDeleteAuthor,
  ): CancelablePromise<Message> {
    const { authorId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/authors/{author_id}",
      path: {
        author_id: authorId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class PolicyService {
  /**
   * Read Policy
   * Retrieve public policy data.
   * @returns PolicyPublic Successful Response
   * @throws ApiError
   */
  public static readPolicy(): CancelablePromise<PolicyPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/policy/",
    })
  }
}

export class ImprintService {
  /**
   * Read Imprint
   * Retrieve public imprint data.
   * @returns ImprintPublic Successful Response
   * @throws ApiError
   */
  public static readImprint(): CancelablePromise<ImprintPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/imprint/",
    })
  }
}

export class AgbService {
  /**
   * Read Agb
   * Retrieve public AGB data.
   * @returns AGBPublic Successful Response
   * @throws ApiError
   */
  public static readAgb(): CancelablePromise<AGBPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/agb/",
    })
  }
}

export type TDataReadPublicationsPublic = {
  authorId?: string | null
  categoryId?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  descending?: boolean
  orderBy?: PublicPublicationsOrderBy
  query?: string | null
  skip?: number
  tagId?: string | null
}
export type TDataReadPublicationPublic = {
  classicId: number
}
export type TDataReadPublications = {
  descending?: boolean
  limit?: number
  orderBy?: PublicationsOrderBy
  query?: string | null
  showOnlyHidden?: boolean
  skip?: number
}
export type TDataCreatePublication = {
  requestBody: PublicationCreate
}
export type TDataReadPublicationById = {
  publicationId: string
}
export type TDataUpdatePublication = {
  publicationId: string
  requestBody: PublicationUpdate
}
export type TDataDeletePublication = {
  publicationId: string
}
export type TDataToggleHidden = {
  publicationId: string
}

export class PublicationsService {
  /**
   * Read Publications
   * Retrieve public publications.
   * No params validation, query will just not match -> 204 response.
   * @returns PublicationsPublic Successful Response
   * @throws ApiError
   */
  public static readPublicationsPublic(
    data: TDataReadPublicationsPublic = {},
  ): CancelablePromise<PublicationsPublic> {
    const {
      authorId,
      categoryId,
      dateFrom,
      dateTo,
      descending = true,
      orderBy = "publication_dt",
      query,
      skip = 0,
      tagId,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/publications/",
      query: {
        skip,
        order_by: orderBy,
        descending,
        category_id: categoryId,
        author_id: authorId,
        tag_id: tagId,
        date_from: dateFrom,
        date_to: dateTo,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Publication
   * Retrieve public publication.
   * @returns PublicationPublic Successful Response
   * @throws ApiError
   */
  public static readPublicationPublic(
    data: TDataReadPublicationPublic,
  ): CancelablePromise<PublicationPublic> {
    const { classicId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/publications/{classic_id}",
      path: {
        classic_id: classicId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Publications
   * Retrieve publications.
   * @returns PublicationsPrivate Successful Response
   * @throws ApiError
   */
  public static readPublications(
    data: TDataReadPublications = {},
  ): CancelablePromise<PublicationsPrivate> {
    const {
      descending = true,
      limit = 100,
      orderBy = "created_at",
      query,
      showOnlyHidden = false,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/publications/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        show_only_hidden: showOnlyHidden,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Publication
   * Create new publication.
   * @returns PublicationPrivate Successful Response
   * @throws ApiError
   */
  public static createPublication(
    data: TDataCreatePublication,
  ): CancelablePromise<PublicationPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/publications/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Publication By Id
   * Get a specific publication by ID.
   * @returns PublicationPrivate Successful Response
   * @throws ApiError
   */
  public static readPublicationById(
    data: TDataReadPublicationById,
  ): CancelablePromise<PublicationPrivate> {
    const { publicationId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/publications/{publication_id}",
      path: {
        publication_id: publicationId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Publication
   * Update an existing publication.
   * @returns PublicationPrivate Successful Response
   * @throws ApiError
   */
  public static updatePublication(
    data: TDataUpdatePublication,
  ): CancelablePromise<PublicationPrivate> {
    const { publicationId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/publications/{publication_id}",
      path: {
        publication_id: publicationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Publication
   * Delete a publication.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deletePublication(
    data: TDataDeletePublication,
  ): CancelablePromise<Message> {
    const { publicationId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/publications/{publication_id}",
      path: {
        publication_id: publicationId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Toggle Hidden
   * @returns PublicationPrivate Successful Response
   * @throws ApiError
   */
  public static toggleHidden(
    data: TDataToggleHidden,
  ): CancelablePromise<PublicationPrivate> {
    const { publicationId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/publications/{publication_id}/toggle-hidden",
      path: {
        publication_id: publicationId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class LandingpageService {
  /**
   * Read Landingpage
   * Retrieve publications for landingpage.
   * @returns LandingpagePublic Successful Response
   * @throws ApiError
   */
  public static readLandingpage(): CancelablePromise<LandingpagePublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/landingpage/",
    })
  }
}

export class RatesService {
  /**
   * Read Rates
   * Retrieve public rates data.
   * @returns RateChartsPublic Successful Response
   * @throws ApiError
   */
  public static readRates(): CancelablePromise<RateChartsPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/rates/",
    })
  }
}

export class NavigationService {
  /**
   * Read Navigation
   * Retrieve public navigation settings.
   * @returns NavigationsPublic Successful Response
   * @throws ApiError
   */
  public static readNavigation(): CancelablePromise<NavigationsPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/navigation/",
    })
  }
}

export type TDataCreateSettingsMimetype = {
  requestBody: SettingsMimeTypeCreate
}
export type TDataReadSettingsMimetypeById = {
  mimetypeId: string
}
export type TDataUpdateSettingsMimetype = {
  mimetypeId: string
  requestBody: SettingsMimeTypeUpdate
}
export type TDataDeleteSettingsMimetype = {
  mimetypeId: string
}

export class SettingsMimetypesService {
  /**
   * Read Settings Mimetypes
   * Retrieve mimetype settings.
   * @returns SettingsMimeTypesPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsMimetypes(): CancelablePromise<SettingsMimeTypesPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/mimetypes/",
    })
  }

  /**
   * Create Settings Mimetype
   * Create new mimetype setting.
   * @returns SettingsMimeTypePrivate Successful Response
   * @throws ApiError
   */
  public static createSettingsMimetype(
    data: TDataCreateSettingsMimetype,
  ): CancelablePromise<SettingsMimeTypePrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/settings/mimetypes/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Settings Mimetype By Id
   * Get a specific mimetype setting by id.
   * @returns SettingsMimeTypePrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsMimetypeById(
    data: TDataReadSettingsMimetypeById,
  ): CancelablePromise<SettingsMimeTypePrivate> {
    const { mimetypeId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/mimetypes/{mimetype_id}",
      path: {
        mimetype_id: mimetypeId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Settings Mimetype
   * Update a mimetype setting.
   * @returns SettingsMimeTypePrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsMimetype(
    data: TDataUpdateSettingsMimetype,
  ): CancelablePromise<SettingsMimeTypePrivate> {
    const { mimetypeId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/mimetypes/{mimetype_id}",
      path: {
        mimetype_id: mimetypeId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Settings Mimetype
   * Delete a mimetype setting.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsMimetype(
    data: TDataDeleteSettingsMimetype,
  ): CancelablePromise<Message> {
    const { mimetypeId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/settings/mimetypes/{mimetype_id}",
      path: {
        mimetype_id: mimetypeId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataUpdateSettingsCommon = {
  requestBody: SettingsCommonUpdate
}

export class SettingsCommonService {
  /**
   * Read Settings Common
   * Retrieve common settings.
   * Single row table.
   * @returns SettingsCommonPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsCommon(): CancelablePromise<SettingsCommonPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/common/",
    })
  }

  /**
   * Update Settings Common
   * Update common settings.
   * @returns SettingsCommonPrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsCommon(
    data: TDataUpdateSettingsCommon,
  ): CancelablePromise<SettingsCommonPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/common/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Next Publish Dt
   * @returns NextPublishDTPrivate Successful Response
   * @throws ApiError
   */
  public static readNextPublishDt(): CancelablePromise<NextPublishDTPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/next-publish-date/",
    })
  }
}

export type TDataUpdateSettingsPriocategories = {
  requestBody: SettingsPrioCategoriesUpdate
}

export class SettingsPriocategoriesService {
  /**
   * Read Settings Priocategories
   * Retrieve all priocategories.
   * @returns SettingsPrioCategoriesPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsPriocategories(): CancelablePromise<SettingsPrioCategoriesPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/priocategories/",
    })
  }

  /**
   * Update Settings Priocategories
   * Update all priocategories.
   * Always sets the entire table to the provided list of categories.
   * @returns SettingsPrioCategoriesPrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsPriocategories(
    data: TDataUpdateSettingsPriocategories,
  ): CancelablePromise<SettingsPrioCategoriesPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/priocategories/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataUpdateSettingsLandingpage = {
  requestBody: SettingsLandingpageUpdateRequest
}

export class SettingsLandingpageService {
  /**
   * Read Settings Landingpage
   * Retrieve all landingpage categories and rubrics.
   * @returns SettingsLandingpageUpdateResponse Successful Response
   * @throws ApiError
   */
  public static readSettingsLandingpage(): CancelablePromise<SettingsLandingpageUpdateResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/landingpage/",
    })
  }

  /**
   * Update Settings Landingpage
   * Update all landingpage categories and rubrics.
   * Always sets the entire table to the provided list of categories and rubrics.
   * @returns SettingsLandingpageUpdateResponse Successful Response
   * @throws ApiError
   */
  public static updateSettingsLandingpage(
    data: TDataUpdateSettingsLandingpage,
  ): CancelablePromise<SettingsLandingpageUpdateResponse> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/landingpage/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataUpdateSettingsSubscription = {
  requestBody: SettingsSubscriptionUpdate
}

export class SettingsSubscriptionsService {
  /**
   * Read Settings Subscription
   * Retrieve subscription settings.
   * Single row table.
   * @returns SettingsSubscriptionPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsSubscription(): CancelablePromise<SettingsSubscriptionPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/subscriptions/",
    })
  }

  /**
   * Update Settings Subscription
   * Update subscription settings.
   * @returns SettingsSubscriptionPrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsSubscription(
    data: TDataUpdateSettingsSubscription,
  ): CancelablePromise<SettingsSubscriptionPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/subscriptions/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateSettingsRatechart = {
  requestBody: SettingsRateChartCreate
}
export type TDataUpdateSettingsRatechart = {
  ratechartId: string
  requestBody: SettingsRateChartUpdate
}
export type TDataDeleteSettingsRatechart = {
  ratechartId: string
}

export class SettingsRatechartsService {
  /**
   * Read Settings Ratecharts
   * Retrieve ratechart settings.
   * @returns SettingsRateChartsPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsRatecharts(): CancelablePromise<SettingsRateChartsPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/ratecharts/",
    })
  }

  /**
   * Create Settings Ratechart
   * Create new ratechart settings.
   * @returns SettingsRateChartPrivate Successful Response
   * @throws ApiError
   */
  public static createSettingsRatechart(
    data: TDataCreateSettingsRatechart,
  ): CancelablePromise<SettingsRateChartPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/settings/ratecharts/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Settings Ratechart
   * Update a ratechart setting.
   * @returns SettingsRateChartPrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsRatechart(
    data: TDataUpdateSettingsRatechart,
  ): CancelablePromise<SettingsRateChartPrivate> {
    const { ratechartId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/ratecharts/{ratechart_id}",
      path: {
        ratechart_id: ratechartId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Settings Ratechart
   * Delete a ratechart setting.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsRatechart(
    data: TDataDeleteSettingsRatechart,
  ): CancelablePromise<Message> {
    const { ratechartId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/settings/ratecharts/{ratechart_id}",
      path: {
        ratechart_id: ratechartId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataUpdateSettingsNavigations = {
  requestBody: SettingsNavigationsUpdate
}

export class SettingsNavigationService {
  /**
   * Read Settings Navigations
   * Retrieve all navigation settings.
   * @returns SettingsNavigationsPrivate Successful Response
   * @throws ApiError
   */
  public static readSettingsNavigations(): CancelablePromise<SettingsNavigationsPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/navigation/",
    })
  }

  /**
   * Update Settings Navigations
   * Update all navigation settings.
   * Always sets the entire table to the provided list of navigation elements.
   * @returns SettingsNavigationsPrivate Successful Response
   * @throws ApiError
   */
  public static updateSettingsNavigations(
    data: TDataUpdateSettingsNavigations,
  ): CancelablePromise<SettingsNavigationsPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/settings/navigation/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class SettingsActionsService {
  /**
   * Action Prune Media
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static actionPruneMedia(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/actions/media/prune",
    })
  }

  /**
   * Action Clear Whole Cache
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static actionClearWholeCache(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/actions/cache/all",
    })
  }

  /**
   * Action Clear Public Cache
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static actionClearPublicCache(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/actions/cache/public",
    })
  }

  /**
   * Action Clear Private Cache
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static actionClearPrivateCache(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/settings/actions/cache/private",
    })
  }
}

export type TDataReadUsers = {
  descending?: boolean
  limit?: number
  orderBy?: UsersOrderBy
  query?: string | null
  skip?: number
}
export type TDataCreateUser = {
  requestBody: UserCreate
}
export type TDataUpdateUserMe = {
  requestBody: UserUpdateMe
}
export type TDataUpdatePasswordMe = {
  requestBody: UpdatePassword
}
export type TDataReadUserById = {
  userId: string
}
export type TDataUpdateUser = {
  requestBody: UserUpdate
  userId: string
}
export type TDataDeleteUser = {
  userId: string
}

export class UsersService {
  /**
   * Read Users
   * Retrieve users.
   * @returns UsersPrivate Successful Response
   * @throws ApiError
   */
  public static readUsers(
    data: TDataReadUsers = {},
  ): CancelablePromise<UsersPrivate> {
    const {
      descending = false,
      limit = 100,
      orderBy = "email",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/users/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create User
   * Create new user.
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static createUser(
    data: TDataCreateUser,
  ): CancelablePromise<UserPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/users/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read User Me
   * Get current user.
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static readUserMe(): CancelablePromise<UserPrivate> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/users/me",
    })
  }

  /**
   * Update User Me
   * Update own user.
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static updateUserMe(
    data: TDataUpdateUserMe,
  ): CancelablePromise<UserPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/users/me",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Password Me
   * Update own password.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static updatePasswordMe(
    data: TDataUpdatePasswordMe,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/users/me/password",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read User By Id
   * Get a specific user by id.
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static readUserById(
    data: TDataReadUserById,
  ): CancelablePromise<UserPrivate> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update User
   * Update a user.
   * @returns UserPrivate Successful Response
   * @throws ApiError
   */
  public static updateUser(
    data: TDataUpdateUser,
  ): CancelablePromise<UserPrivate> {
    const { requestBody, userId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/users/{user_id}",
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete User
   * Delete a user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUser(data: TDataDeleteUser): CancelablePromise<Message> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadMedia = {
  descending?: boolean
  limit?: number
  orderBy?: MediaOrderBy
  query?: string | null
  skip?: number
}
export type TDataCreateMedia = {
  formData: Body_media_create_media
}
export type TDataReadMediaById = {
  mediaId: string
}
export type TDataUpdateFocalPoint = {
  mediaId: string
  requestBody: Array<number>
}

export class MediaService {
  /**
   * Read Media
   * Retrieve media items.
   * @returns MediaPreview Successful Response
   * @throws ApiError
   */
  public static readMedia(
    data: TDataReadMedia = {},
  ): CancelablePromise<MediaPreview> {
    const {
      descending = true,
      limit = 100,
      orderBy = "created_at",
      query,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/media/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Media
   * Create new media.
   * @returns MediaItem Successful Response
   * @throws ApiError
   */
  public static createMedia(
    data: TDataCreateMedia,
  ): CancelablePromise<MediaItem> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/media/",
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Media By Id
   * Get a specific media item by id.
   * @returns MediaItem Successful Response
   * @throws ApiError
   */
  public static readMediaById(
    data: TDataReadMediaById,
  ): CancelablePromise<MediaItem> {
    const { mediaId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/media/{media_id}",
      path: {
        media_id: mediaId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Focal Point
   * Update focal point of media item.
   * @returns MediaItem Successful Response
   * @throws ApiError
   */
  public static updateFocalPoint(
    data: TDataUpdateFocalPoint,
  ): CancelablePromise<MediaItem> {
    const { mediaId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/media/{media_id}/focal",
      path: {
        media_id: mediaId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadAuthorgroups = {
  descending?: boolean
  limit?: number
  orderBy?: AuthorGroupsOrderBy
  query?: string | null
  showInactive?: boolean
  skip?: number
}
export type TDataCreateAuthorgroup = {
  requestBody: AuthorGroupCreate
}
export type TDataReadAuthorgroupById = {
  authorgroupId: string
}
export type TDataUpdateAuthorgroup = {
  authorgroupId: string
  requestBody: AuthorGroupUpdate
}
export type TDataDeleteAuthorgroup = {
  authorgroupId: string
}

export class AuthorgroupsService {
  /**
   * Read Authorgroups
   * Retrieve authorgroups.
   * @returns AuthorGroups Successful Response
   * @throws ApiError
   */
  public static readAuthorgroups(
    data: TDataReadAuthorgroups = {},
  ): CancelablePromise<AuthorGroups> {
    const {
      descending = false,
      limit = 100,
      orderBy = "name",
      query,
      showInactive = true,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/authorgroups/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        show_inactive: showInactive,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Authorgroup
   * Create new authorgroup.
   * @returns AuthorGroup Successful Response
   * @throws ApiError
   */
  public static createAuthorgroup(
    data: TDataCreateAuthorgroup,
  ): CancelablePromise<AuthorGroup> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/authorgroups/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Authorgroup By Id
   * Get a specific authorgroup by ID.
   * @returns AuthorGroupPrivate Successful Response
   * @throws ApiError
   */
  public static readAuthorgroupById(
    data: TDataReadAuthorgroupById,
  ): CancelablePromise<AuthorGroupPrivate> {
    const { authorgroupId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/authorgroups/{authorgroup_id}",
      path: {
        authorgroup_id: authorgroupId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Authorgroup
   * Update an existing authorgroup.
   * @returns AuthorGroup Successful Response
   * @throws ApiError
   */
  public static updateAuthorgroup(
    data: TDataUpdateAuthorgroup,
  ): CancelablePromise<AuthorGroup> {
    const { authorgroupId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/authorgroups/{authorgroup_id}",
      path: {
        authorgroup_id: authorgroupId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Authorgroup
   * Delete an authorgroup.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAuthorgroup(
    data: TDataDeleteAuthorgroup,
  ): CancelablePromise<Message> {
    const { authorgroupId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/authorgroups/{authorgroup_id}",
      path: {
        authorgroup_id: authorgroupId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadPerms = {
  descending?: boolean
  orderBy?: PermsOrderBy
  query?: string | null
}

export class PermissionsService {
  /**
   * Read Perms
   * Retrieve permissions.
   * @returns PermissionsPrivate Successful Response
   * @throws ApiError
   */
  public static readPerms(
    data: TDataReadPerms = {},
  ): CancelablePromise<PermissionsPrivate> {
    const { descending = false, orderBy = "description", query } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/perms/",
      query: {
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadRoles = {
  descending?: boolean
  orderBy?: RolesOrderBy
  query?: string | null
}
export type TDataCreateRole = {
  requestBody: RoleCreate
}
export type TDataReadRoleById = {
  roleId: string
}
export type TDataUpdateRole = {
  requestBody: RoleUpdate
  roleId: string
}
export type TDataDeleteRole = {
  roleId: string
}

export class RolesService {
  /**
   * Read Roles
   * Retrieve roles.
   * @returns RolesPreview Successful Response
   * @throws ApiError
   */
  public static readRoles(
    data: TDataReadRoles = {},
  ): CancelablePromise<RolesPreview> {
    const { descending = false, orderBy = "name", query } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/roles/",
      query: {
        order_by: orderBy,
        descending,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Role
   * Create new Role.
   * @returns RolePrivate Successful Response
   * @throws ApiError
   */
  public static createRole(
    data: TDataCreateRole,
  ): CancelablePromise<RolePrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/roles/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Role By Id
   * Get a specific role by ID.
   * @returns RolePrivate Successful Response
   * @throws ApiError
   */
  public static readRoleById(
    data: TDataReadRoleById,
  ): CancelablePromise<RolePrivate> {
    const { roleId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/roles/{role_id}",
      path: {
        role_id: roleId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Role
   * Update an existing Role.
   * @returns RolePrivate Successful Response
   * @throws ApiError
   */
  public static updateRole(
    data: TDataUpdateRole,
  ): CancelablePromise<RolePrivate> {
    const { requestBody, roleId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/roles/{role_id}",
      path: {
        role_id: roleId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Role
   * Delete a Role.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteRole(data: TDataDeleteRole): CancelablePromise<Message> {
    const { roleId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/roles/{role_id}",
      path: {
        role_id: roleId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadCategories = {
  descending?: boolean
  limit?: number
  orderBy?: CategoriesOrderBy
  query?: string | null
  showInactive?: boolean
  skip?: number
}
export type TDataCreateCategory = {
  requestBody: CategoryCreate
}
export type TDataReadCategoryById = {
  categoryId: string
}
export type TDataUpdateCategory = {
  categoryId: string
  requestBody: CategoryUpdate
}
export type TDataDeleteCategory = {
  categoryId: string
}

export class CategoriesService {
  /**
   * Read Categories
   * Retrieve categories.
   * @returns CategoriesPrivate Successful Response
   * @throws ApiError
   */
  public static readCategories(
    data: TDataReadCategories = {},
  ): CancelablePromise<CategoriesPrivate> {
    const {
      descending = false,
      limit = 100,
      orderBy = "name",
      query,
      showInactive = true,
      skip = 0,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/categories/",
      query: {
        skip,
        limit,
        order_by: orderBy,
        descending,
        show_inactive: showInactive,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Category
   * Create new category.
   * @returns CategoryPrivate Successful Response
   * @throws ApiError
   */
  public static createCategory(
    data: TDataCreateCategory,
  ): CancelablePromise<CategoryPrivate> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reda/categories/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Category By Id
   * Get a specific category by id.
   * @returns CategoryPrivate Successful Response
   * @throws ApiError
   */
  public static readCategoryById(
    data: TDataReadCategoryById,
  ): CancelablePromise<CategoryPrivate> {
    const { categoryId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/reda/categories/{category_id}",
      path: {
        category_id: categoryId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Category
   * Update a category.
   * @returns CategoryPrivate Successful Response
   * @throws ApiError
   */
  public static updateCategory(
    data: TDataUpdateCategory,
  ): CancelablePromise<CategoryPrivate> {
    const { categoryId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/reda/categories/{category_id}",
      path: {
        category_id: categoryId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Category
   * Delete a category.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteCategory(
    data: TDataDeleteCategory,
  ): CancelablePromise<Message> {
    const { categoryId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/reda/categories/{category_id}",
      path: {
        category_id: categoryId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataDeleteTagByName = {
  requestBody: Array<string>
}
export type TDataDeleteAuthorgroupById = {
  requestBody: Array<string>
}
export type TDataDeleteAuthorsById = {
  requestBody: Array<string>
}
export type TDataDeleteMediaById = {
  requestBody: Array<string>
}
export type TDataDeleteUserById = {
  requestBody: Array<string>
}
export type TDataDeleteCategoriesByName = {
  requestBody: Array<string>
}
export type TDataDeletePublicationsById = {
  requestBody: Array<string>
}
export type TDataDeleteSettingsMimetypesById = {
  requestBody: Array<string>
}
export type TDataDeleteSettingsRatechartsById = {
  requestBody: Array<string>
}
export type TDataDeleteFormsContactsById = {
  requestBody: Array<string>
}
export type TDataDeleteFormsNewslettersById = {
  requestBody: Array<string>
}
export type TDataDeleteFormsSubscriptionsById = {
  requestBody: Array<string>
}

export class DevService {
  /**
   * Delete All Tags
   * Delete all tags.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllTags(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/tags/",
    })
  }

  /**
   * Delete Tag By Name
   * Delete tag by given name.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteTagByName(
    data: TDataDeleteTagByName,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/tags/name",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Authorgroups
   * Delete all authorgroups.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllAuthorgroups(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/authorgroups/",
    })
  }

  /**
   * Delete Authorgroup By Id
   * Delete author groups by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAuthorgroupById(
    data: TDataDeleteAuthorgroupById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/authorgroups/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Authors
   * Delete all authors.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllAuthors(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/authors/",
    })
  }

  /**
   * Delete Authors By Id
   * Delete authors by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAuthorsById(
    data: TDataDeleteAuthorsById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/authors/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Media
   * Delete all media items.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllMedia(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/media/",
    })
  }

  /**
   * Delete Media By Id
   * Delete media items by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteMediaById(
    data: TDataDeleteMediaById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/media/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Users
   * Delete all users.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllUsers(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/users/",
    })
  }

  /**
   * Delete User By Id
   * Delete users by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUserById(
    data: TDataDeleteUserById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/users/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Categories
   * Delete all categories.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllCategories(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/categories/",
    })
  }

  /**
   * Delete Categories By Name
   * Delete categories by given names.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteCategoriesByName(
    data: TDataDeleteCategoriesByName,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/categories/name",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Publications
   * Delete all publications.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllPublications(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/publications/",
    })
  }

  /**
   * Delete Publications By Id
   * Delete publications by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deletePublicationsById(
    data: TDataDeletePublicationsById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/publications/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Settings Mimetypes
   * Delete all mimetype settings.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllSettingsMimetypes(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/mimetypes/",
    })
  }

  /**
   * Delete Settings Mimetypes By Id
   * Delete mimetype settings by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsMimetypesById(
    data: TDataDeleteSettingsMimetypesById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/mimetypes/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Settings Priocategories
   * Delete all priocategory settings.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsPriocategories(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/priocategories/",
    })
  }

  /**
   * Delete Settings Landingpage
   * Delete all landingpage settings.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsLandingpage(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/landingpage/",
    })
  }

  /**
   * Delete All Settings Ratecharts
   * Delete all ratechart settings.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllSettingsRatecharts(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/ratecharts/",
    })
  }

  /**
   * Delete Settings Ratecharts By Id
   * Delete ratechart settings by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsRatechartsById(
    data: TDataDeleteSettingsRatechartsById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/ratecharts/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Settings Navigation
   * Delete all navigation settings.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteSettingsNavigation(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/settings/navigation/",
    })
  }

  /**
   * Delete All Forms Contacts
   * Delete all contact form submissions.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllFormsContacts(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/contact/",
    })
  }

  /**
   * Delete Forms Contacts By Id
   * Delete contact form submissions by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsContactsById(
    data: TDataDeleteFormsContactsById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/contact/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Forms Newsletters
   * Delete all newsletter subscriptions.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllFormsNewsletters(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/newsletter/",
    })
  }

  /**
   * Delete Forms Newsletters By Id
   * Delete newsletter subscriptions by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsNewslettersById(
    data: TDataDeleteFormsNewslettersById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/newsletter/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete All Forms Subscriptions
   * Delete all subscription form submissions.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteAllFormsSubscriptions(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/subscription/",
    })
  }

  /**
   * Delete Forms Subscriptions By Id
   * Delete subscription form submissions by id.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteFormsSubscriptionsById(
    data: TDataDeleteFormsSubscriptionsById,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/dev/forms/subscription/ids",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}
